import { gateway } from "../../../gateway";
import { GroupNumberValidation, MessageGroupChannel, MessageGroupMemberInput, MessageGroupMemberType } from "../messageGroups.types";

const unwrapApiData = (res: any) => res?.data?.data ?? res?.data;

const buildCompanyHeaders = (companyId?: number | string | null) => (companyId != null ? { "x-company-id": String(companyId) } : {});

export const normalizeGroupMessage = (message: any) => {
  if (!message) return message;
  const sender = message.sender || {};
  const fullName = sender.name || "";
  const [firstName, ...rest] = fullName.split(" ");
  const content = message.content || {};
  const category = content.category === "text" ? "plainText" : content.category;

  return {
    ...message,
    sender: {
      ...sender,
      firstName: sender.firstName ?? firstName ?? "",
      lastName: sender.lastName ?? rest.join(" ") ?? "",
      avatar: sender.avatar ?? sender.avatarUrl ?? null,
    },
    content: {
      ...content,
      category,
    },
    mentions: Array.isArray(message.mentions) ? message.mentions : [],
  };
};

export const getMessageGroups = async (token: string, { page = 1, pageSize = 50, companyId = undefined as number | string | null | undefined } = {}) => {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("pageSize", String(pageSize));

  const res = await gateway.get(`/message-groups?${params.toString()}`, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const getMessageGroupSummary = async (token: string, groupUid: string, companyId?: number | string | null) => {
  const res = await gateway.get(`/message-groups/${groupUid}`, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const getMessageGroupMessages = async (
  token: string,
  groupUid: string,
  { page = 1, pageSize = 20, order = "DESC", companyId = undefined as number | string | null | undefined } = {},
) => {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("pageSize", String(pageSize));
  params.append("order", order);

  const res = await gateway.get(`/message-groups/${groupUid}/messages?${params.toString()}`, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });

  const data = unwrapApiData(res);
  return {
    ...data,
    items: (data?.items || []).map(normalizeGroupMessage),
  };
};

export const sendMessageGroupMessage = async (token: string, groupUid: string, messageData: any, companyId?: number | string | null) => {
  const formData = new FormData();
  formData.append("name", messageData.name || "Text");

  if (messageData.plainText) {
    formData.append("plainText", messageData.plainText);
  }
  if (messageData.file) {
    formData.append("file", messageData.file);
  }
  if (messageData.mentions?.length) {
    formData.append("mentions", JSON.stringify(messageData.mentions));
  }

  const res = await gateway.post(`/message-groups/${groupUid}/messages`, formData, {
    headers: {
      Authorization: `${token}`,
      ...buildCompanyHeaders(companyId),
      "Content-Type": "multipart/form-data",
    },
  });

  return unwrapApiData(res);
};

export const markMessageGroupRead = async (token: string, groupUid: string, companyId?: number | string | null) => {
  const res = await gateway.post(`/message-groups/${groupUid}/read`, null, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const createMessageGroup = async (
  token: string,
  payload: {
    name: string;
    avatar?: string | null;
    description?: string | null;
    channel?: MessageGroupChannel;
    smsFromNumber?: string | null;
    members: MessageGroupMemberInput[];
  },
  companyId?: number | string | null,
) => {
  const res = await gateway.post("/message-groups", payload, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const updateMessageGroup = async (
  token: string,
  groupUid: string,
  payload: { name?: string; avatar?: string | null; description?: string | null; smsFromNumber?: string | null },
  companyId?: number | string | null,
) => {
  const res = await gateway.patch(`/message-groups/${groupUid}`, payload, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const validateGroupNumbers = async (
  token: string,
  numbers: string[],
  companyId?: number | string | null,
): Promise<GroupNumberValidation[]> => {
  const res = await gateway.post(
    "/message-groups/validate-numbers",
    { numbers },
    { headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) } },
  );

  return unwrapApiData(res) ?? [];
};

export const deleteMessageGroup = async (token: string, groupUid: string, companyId?: number | string | null) => {
  const res = await gateway.delete(`/message-groups/${groupUid}`, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const addMessageGroupMembers = async (
  token: string,
  groupUid: string,
  members: MessageGroupMemberInput[],
  companyId?: number | string | null,
) => {
  const res = await gateway.post(
    `/message-groups/${groupUid}/members`,
    { members },
    { headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) } },
  );
  return unwrapApiData(res);
};

export const removeMessageGroupMember = async (
  token: string,
  groupUid: string,
  member: { type: MessageGroupMemberType; uid: string },
  companyId?: number | string | null,
) => {
  const res = await gateway.delete(`/message-groups/${groupUid}/members/${member.uid}?memberType=${member.type}`, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const leaveMessageGroup = async (token: string, groupUid: string, companyId?: number | string | null) => {
  const res = await gateway.post(`/message-groups/${groupUid}/leave`, null, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const upsertMessageInPages = (oldData: any, message: any) => {
  if (!message) return oldData;
  if (!oldData || !Array.isArray(oldData.pages)) {
    return { pages: [{ items: [message] }], pageParams: [1] };
  }
  const exists = oldData.pages.some((page: any) => (page?.items || []).some((item: any) => item.uid === message.uid));
  if (exists) return oldData;

  const pages = [...oldData.pages];
  const first = pages[0] || { items: [] };
  pages[0] = { ...first, items: [message, ...(first.items || [])] };
  return { ...oldData, pages };
};

export const bumpMessageGroupListItem = (
  oldData: any,
  groupUid: string,
  options: { lastMessage?: string | null; lastMessageAt?: string | null; lastMessageSenderName?: string | null; incrementUnread?: boolean; mentionsMe?: boolean } = {},
) => {
  const { lastMessage, lastMessageAt, lastMessageSenderName, incrementUnread, mentionsMe } = options;
  if (!oldData || !Array.isArray(oldData.items)) return oldData;
  const index = oldData.items.findIndex((item: any) => item.groupUid === groupUid);
  if (index === -1) return oldData;

  const item = oldData.items[index];
  const updated = {
    ...item,
    lastMessage: lastMessage ?? item.lastMessage,
    lastMessageAt: lastMessageAt ?? item.lastMessageAt,
    lastMessageSenderName: lastMessageSenderName ?? item.lastMessageSenderName,
    unreadCount: incrementUnread ? (item.unreadCount || 0) + 1 : item.unreadCount,
    mentionsMe: mentionsMe ? true : item.mentionsMe,
  };

  return { ...oldData, items: [updated, ...oldData.items.slice(0, index), ...oldData.items.slice(index + 1)] };
};

export const clearMessageGroupUnread = (oldData: any, groupUid: string) => {
  if (!oldData || !Array.isArray(oldData.items)) return oldData;
  let changed = false;
  const items = oldData.items.map((item: any) => {
    if (item.groupUid === groupUid && (item.unreadCount || item.mentionsMe)) {
      changed = true;
      return { ...item, unreadCount: 0, mentionsMe: false };
    }
    return item;
  });
  return changed ? { ...oldData, items } : oldData;
};
